// ═══════════════════════════════════════════════════
//  Metal Base finish options
//
//  Stainless finishes are purely descriptive — they say what the metal is and
//  never affect cost. Painted finishes may carry their own cost, flagged by
//  `requiresCost`, which is what reveals the paint $/LF field in the modal.
//
//  Testing `requiresCost` rather than `key === 'custom'` is deliberate: adding
//  a fixed-price paint finish later is then a change to this list alone, not a
//  change to the modal's conditional logic.
//
//  Deliberately not a database table — see
//  docs/superpowers/specs/2026-08-04-cria-metal-base-template-design.md
// ═══════════════════════════════════════════════════

export type MetalType = 'stainless' | 'painted';

export interface MetalFinishOption {
  /** Stable identifier persisted in metal_base_config.finish. Never a label. */
  key: string;
  label: string;
  /** Whether choosing this finish requires the estimator to enter a paint $/LF. */
  requiresCost: boolean;
}

export const STAINLESS_FINISHES: readonly MetalFinishOption[] = [
  { key: 'polished', label: 'Polished Stainless Steel', requiresCost: false },
  { key: 'satin', label: 'Satin Stainless Steel', requiresCost: false },
  { key: 'brushed', label: 'Brushed Stainless Steel', requiresCost: false },
];

export const PAINT_FINISHES: readonly MetalFinishOption[] = [
  { key: 'custom', label: 'Custom Finish', requiresCost: true },
];

/** The finish options valid for one metal type. */
export function metalFinishesFor(metalType: MetalType): readonly MetalFinishOption[] {
  return metalType === 'painted' ? PAINT_FINISHES : STAINLESS_FINISHES;
}

/**
 * Resolve a stored finish key within its metal type.
 *
 * Returns null for an unknown key rather than throwing: a config saved before a
 * finish was renamed should still price its metal, and the caller reports the
 * unresolved finish as a note.
 */
export function findMetalFinish(
  metalType: MetalType,
  key: string | null,
): MetalFinishOption | null {
  if (!key) return null;
  return metalFinishesFor(metalType).find((f) => f.key === key) ?? null;
}
